import type { PoGetSystemStatusResponse } from '@repo/sdk/gen'
import { Alert, AlertDescription } from '@repo/ui/alert'
import { formatLocaleDate } from '@repo/ui/dates'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Database, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import {
	datasetUpToDate,
	failedTables,
	updateInProgress
} from '@/lib/data-freshness'
import { useTranslation } from '@/lib/i18n'
import { NOTICE_TONES } from '@/routes/(private)/documents/-lib/notice-tones'
import { systemStatusQueryOptions } from '@/routes/(private)/documents/-lib/query-options'

// The data «Genera» will read, stated next to it: the last complete sync, never
// one still in flight. The button stays live during an update (decision 8) and
// says which data it will use; without any complete sync, or without a table
// every document needs, generation is disabled and the note explains why.
// «aggiornati al» is said only when the data is whole and the last update came
// through (datasetUpToDate, the dashboard's condition too); otherwise the date
// is stated plainly and the warnings name what is left out, left behind or
// failed. The status is not awaited by the route, so the note may arrive after
// the dialog opens, or be missing when the status fails.
function DatasetNote() {
	const { t } = useTranslation()
	const status = useQuery(systemStatusQueryOptions())

	if (!status.data) {
		return status.isError ? (
			<DatasetFact>{t('po:index.dataset_unavailable')}</DatasetFact>
		) : null
	}

	const dataset = status.data.dataset
	const updating = updateInProgress(status.data)

	if (dataset.asOf === null) {
		return (
			<DatasetWarning icon={<AlertTriangle />}>
				{t(
					updating
						? 'po:index.no_complete_dataset_syncing'
						: 'po:index.no_complete_dataset'
				)}
			</DatasetWarning>
		)
	}

	// No date to offer: the document cannot be generated on this dataset.
	if (dataset.missingRequired.length > 0) {
		return (
			<div className='flex flex-col gap-2'>
				<DatasetGaps dataset={dataset} />
			</div>
		)
	}

	const date = formatLocaleDate(dataset.asOf, 'P HH:mm')
	// A table left behind already has its own warning, with its date.
	const failed = failedTables(status.data).filter(
		(name) =>
			!dataset.tablesBehind.some((table) => table.tableName === name)
	)
	return (
		<div className='flex flex-col gap-2'>
			{updating ? (
				<DatasetWarning icon={<RefreshCw />}>
					{t('po:index.dataset_syncing', { date })}
				</DatasetWarning>
			) : (
				<DatasetFact>
					{t(
						datasetUpToDate(status.data)
							? 'po:index.dataset_as_of'
							: 'po:index.dataset_date',
						{ date }
					)}
				</DatasetFact>
			)}
			<DatasetGaps dataset={dataset} />
			{failed.length > 0 && (
				<DatasetWarning icon={<AlertTriangle />}>
					{t('po:index.update_failed', { list: failed.join(', ') })}
				</DatasetWarning>
			)}
			{dataset.tablesBehind.length > 0 && (
				<DatasetWarning icon={<AlertTriangle />}>
					{[
						...dataset.tablesBehind.map((table) =>
							t('po:dataset.table_behind', {
								table: table.tableName,
								date: formatLocaleDate(
									table.confirmedAt,
									'P HH:mm'
								)
							})
						),
						t('po:index.tables_behind_effect')
					].join(' ')}
				</DatasetWarning>
			)}
		</div>
	)
}

function DatasetFact(props: { children: ReactNode }) {
	return (
		<p className='flex items-start gap-2 text-muted-foreground text-sm'>
			<Database className='mt-0.5 size-4 shrink-0' />
			{props.children}
		</p>
	)
}

// What keeps the next document from reading every table, said the same way on
// the dashboard and next to «Genera»: a required table missing (generation is
// refused) and tables the server has no copy of (left out until the agent
// sends them again, on a full sync the support requests). A required table the
// server lost is in both lists: the second one says how it comes back.
function DatasetGaps(props: {
	dataset: PoGetSystemStatusResponse['data']['dataset']
}) {
	const { t } = useTranslation()
	const dataset = props.dataset

	return (
		<>
			{dataset.missingRequired.length > 0 && (
				<DatasetWarning icon={<AlertTriangle />}>
					{t('po:dataset.missing_required', {
						list: dataset.missingRequired.join(', ')
					})}
				</DatasetWarning>
			)}
			{dataset.unresolved.length > 0 && (
				<DatasetWarning icon={<AlertTriangle />}>
					{t('po:dataset.unresolved', {
						list: dataset.unresolved.join(', ')
					})}
				</DatasetWarning>
			)}
		</>
	)
}

function DatasetWarning(props: { icon: ReactNode; children: ReactNode }) {
	return (
		<Alert className={NOTICE_TONES.amber}>
			{props.icon}
			<AlertDescription className='text-current'>
				{props.children}
			</AlertDescription>
		</Alert>
	)
}

export { DatasetGaps, DatasetNote }
